import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Save, Upload, FileCheck, CreditCard, Building, FileText, Eye, CheckCircle, AlertCircle } from 'lucide-react';

function DocUpload({ label, icon: Icon, docType, document, onUpload, loading }) {
  const inputRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['.pdf', '.jpg', '.jpeg', '.png'];
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!allowed.includes(ext)) {
      toast.error('Only PDF, JPG, PNG files allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Max 5MB.');
      return;
    }
    await onUpload(docType, file);
    e.target.value = '';
  };

  const viewDoc = () => {
    if (document?.filename) {
      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/kyc/document/${document.filename}`, '_blank');
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="border border-border rounded-sm p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
          <span className="text-sm font-medium text-foreground">{label}</span>
        </div>
        {document ? (
          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
            <CheckCircle className="w-3 h-3 mr-1" /> Uploaded
          </Badge>
        ) : (
          <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-xs">
            <AlertCircle className="w-3 h-3 mr-1" /> Required
          </Badge>
        )}
      </div>

      {document ? (
        <div className="bg-muted rounded-sm p-3 mb-3">
          <p className="text-xs text-muted-foreground font-medium truncate">{document.original_name}</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            {formatSize(document.size)} &bull; {new Date(document.uploaded_at).toLocaleDateString()}
          </p>
        </div>
      ) : (
        <div className="bg-muted border-2 border-dashed border-border rounded-sm p-4 text-center mb-3">
          <FileText className="w-6 h-6 text-slate-300 mx-auto mb-1" strokeWidth={1.5} />
          <p className="text-xs text-muted-foreground">No document uploaded</p>
        </div>
      )}

      <div className="flex gap-2">
        <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFile} className="hidden" data-testid={`${docType}-file-input`} />
        <Button
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
          disabled={loading}
          className="rounded-sm flex-1 text-xs"
          data-testid={`upload-${docType}-btn`}
        >
          <Upload className="w-3 h-3 mr-1" strokeWidth={1.5} />
          {loading ? 'Uploading...' : document ? 'Replace' : 'Upload'}
        </Button>
        {document && (
          <Button variant="ghost" size="sm" onClick={viewDoc} className="rounded-sm text-xs text-[#002FA7]" data-testid={`view-${docType}-btn`}>
            <Eye className="w-3 h-3 mr-1" strokeWidth={1.5} /> View
          </Button>
        )}
      </div>
    </div>
  );
}

export default function KycPage() {
  const [kyc, setKyc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState('');
  const [form, setForm] = useState({
    bank_account_number: '',
    ifsc_code: '',
    bank_name: '',
    pan_number: '',
    aadhaar_number: '',
  });

  const loadKyc = useCallback(async () => {
    try {
      const res = await api.get('/kyc');
      setKyc(res.data);
      setForm({
        bank_account_number: res.data.bank_account_number || '',
        ifsc_code: res.data.ifsc_code || '',
        bank_name: res.data.bank_name || '',
        pan_number: res.data.pan_number || '',
        aadhaar_number: res.data.aadhaar_number || '',
      });
    } catch {
      toast.error('Failed to load KYC details');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadKyc(); }, [loadKyc]);

  const handleSave = async () => {
    if (!form.bank_account_number || !form.ifsc_code) {
      toast.error('Bank account number and IFSC code are required');
      return;
    }
    setSaving(true);
    try {
      const res = await api.put('/kyc/bank-details', form);
      setKyc(res.data);
      toast.success('Bank details saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (docType, file) => {
    setUploading(docType);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/kyc/upload/${docType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setKyc(res.data);
      toast.success(`${docType === 'pan' ? 'PAN' : 'Aadhaar'} card uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading('');
    }
  };

  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const completionItems = [
    { label: 'Bank Account', done: !!kyc?.bank_account_number },
    { label: 'IFSC Code', done: !!kyc?.ifsc_code },
    { label: 'PAN Number', done: !!kyc?.pan_number },
    { label: 'Aadhaar Number', done: !!kyc?.aadhaar_number },
    { label: 'PAN Document', done: !!kyc?.pan_document },
    { label: 'Aadhaar Document', done: !!kyc?.aadhaar_document },
  ];
  const completed = completionItems.filter(i => i.done).length;
  const total = completionItems.length;
  const pct = Math.round((completed / total) * 100);

  if (loading) return <div className="text-sm text-muted-foreground">Loading KYC details...</div>;

  return (
    <div data-testid="kyc-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-heading font-bold text-foreground tracking-tight">KYC Details</h2>
          <p className="text-sm text-muted-foreground font-body mt-1">Manage your bank and identity documents</p>
        </div>
        <Badge
          className={pct === 100
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
            : 'bg-amber-50 text-amber-700 border-amber-200'
          }
          data-testid="kyc-completion-badge"
        >
          {pct === 100 ? <CheckCircle className="w-3 h-3 mr-1" /> : <AlertCircle className="w-3 h-3 mr-1" />}
          {completed}/{total} Complete
        </Badge>
      </div>

      {/* Progress Bar */}
      <div className="border border-border bg-card rounded-sm p-4 mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-muted-foreground uppercase tracking-widest font-body">KYC Completion</span>
          <span className="text-xs font-mono text-muted-foreground">{pct}%</span>
        </div>
        <div className="w-full bg-muted rounded-sm h-2">
          <div
            className={`h-2 rounded-sm transition-all duration-500 ${pct === 100 ? 'bg-emerald-500' : 'bg-[#002FA7]'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="flex flex-wrap gap-3 mt-3">
          {completionItems.map(item => (
            <div key={item.label} className="flex items-center gap-1.5 text-xs">
              {item.done ? (
                <CheckCircle className="w-3 h-3 text-emerald-500" strokeWidth={2} />
              ) : (
                <AlertCircle className="w-3 h-3 text-slate-300" strokeWidth={2} />
              )}
              <span className={item.done ? 'text-muted-foreground' : 'text-muted-foreground'}>{item.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Bank Details */}
        <div className="border border-border bg-card rounded-sm p-6">
          <h3 className="font-heading font-semibold text-foreground mb-4">
            <Building className="w-4 h-4 inline mr-2" strokeWidth={1.5} />
            Bank Details
          </h3>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Bank Name</Label>
              <Input value={form.bank_name} onChange={e => set('bank_name', e.target.value)} placeholder="e.g., State Bank of India" className="rounded-sm" data-testid="bank-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Account Number *</Label>
              <div className="relative">
                <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                <Input value={form.bank_account_number} onChange={e => set('bank_account_number', e.target.value)} placeholder="Enter account number" className="pl-10 rounded-sm font-mono" data-testid="bank-account-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">IFSC Code *</Label>
              <Input value={form.ifsc_code} onChange={e => set('ifsc_code', e.target.value.toUpperCase())} placeholder="e.g., SBIN0001234" className="rounded-sm font-mono uppercase" data-testid="ifsc-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">PAN Number</Label>
              <Input value={form.pan_number} onChange={e => set('pan_number', e.target.value.toUpperCase())} placeholder="e.g., ABCDE1234F" className="rounded-sm font-mono uppercase" maxLength={10} data-testid="pan-number-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Aadhaar Number</Label>
              <Input value={form.aadhaar_number} onChange={e => set('aadhaar_number', e.target.value.replace(/\D/g, ''))} placeholder="e.g., 1234 5678 9012" className="rounded-sm font-mono" maxLength={12} data-testid="aadhaar-number-input" />
            </div>
            <Button onClick={handleSave} disabled={saving} className="bg-[#002FA7] hover:bg-[#002585] rounded-sm w-full" data-testid="save-bank-details-btn">
              <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />
              {saving ? 'Saving...' : 'Save Bank Details'}
            </Button>
          </div>
        </div>

        {/* Document Uploads */}
        <div className="border border-border bg-card rounded-sm p-6">
          <h3 className="font-heading font-semibold text-foreground mb-4">
            <FileCheck className="w-4 h-4 inline mr-2" strokeWidth={1.5} />
            Identity Documents
          </h3>
          <p className="text-xs text-muted-foreground mb-4">Upload clear scans or photos of your documents. Accepted formats: PDF, JPG, PNG (max 5MB)</p>
          <div className="space-y-4">
            <DocUpload
              label="PAN Card"
              icon={CreditCard}
              docType="pan"
              document={kyc?.pan_document}
              onUpload={handleUpload}
              loading={uploading === 'pan'}
            />
            <DocUpload
              label="Aadhaar Card"
              icon={FileCheck}
              docType="aadhaar"
              document={kyc?.aadhaar_document}
              onUpload={handleUpload}
              loading={uploading === 'aadhaar'}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
